import React, { useState } from 'react';
import CompactMealCard from 'components/shared/CompactMealCard';
import ScrollArrows from 'components/shared/ScrollArrows';
import AddonInfoModal from 'components/storefront/AddonInfoModal';
import { useScrollArrows } from 'hooks/useScrollArrows';
import { quizNutritionForMealType } from 'utils/nutritionGoals';

// Same scroll-row key the a-la-carte menu uses per section (e.g. "addons" is
// one of its section ids) — reusing the identical hook/component here too.
const ADDONS_SCROLL_KEY = 'addons';

// Rough wireframe pricing — placeholders until Yang confirms where the real
// per-meal base prices and the subscription discount should live (Airtable?).
export const SUBSCRIPTION_BASE_PRICES = {
  breakfast: 12.99,
  lunch: 19.99,
  dinner: 23.99,
  snack: 4.99,
};
export const SUBSCRIPTION_DISCOUNT = 0.9;

// `mealType` is the canonical singular form (matches constants/meals.js and
// quizAPI-control's mealBreakdown keys via quizNutritionForMealType) — kept
// separate from `label`, which is plural for snacks for display.
export const MEAL_TYPES = [
  { key: 'breakfast', label: 'Breakfast', mealType: 'Breakfast' },
  { key: 'lunch', label: 'Lunch', mealType: 'Lunch' },
  { key: 'dinner', label: 'Dinner', mealType: 'Dinner' },
  { key: 'snack', label: 'Snacks', mealType: 'Snack' },
];

/** Same priority as MenuPage's calorieInfoForMember, minus the Skip Portioning
 *  case — there's no per-dish catalog here, just meal-type quantities. */
export function nutritionInfoForMealType(mealType, householdMember, defaultMealTargets = {}) {
  const fallback = defaultMealTargets[mealType];
  return quizNutritionForMealType(mealType, householdMember)
    ?? (fallback ? { source: 'default', ...fallback } : null);
}

export function createDefaultSubscriptionMember(overrides = {}) {
  return {
    meals: { breakfast: 0, lunch: 1, dinner: 1, snack: 0 },
    addons: {}, // dishId -> qty
    ...overrides,
  };
}

// `addons` is the real catalog — /api/menu's dishes filtered to mealType
// "Add On" (see SubscriptionBuilderPage) — not mock data, so this needs it
// passed in rather than reading a hardcoded list.
export function memberSubtotalCents(member, addons = []) {
  const mealCents = MEAL_TYPES.reduce((sum, { key }) => {
    const qty = member.meals?.[key] || 0;
    return sum + qty * SUBSCRIPTION_BASE_PRICES[key] * 100;
  }, 0);
  const discountedMealCents = mealCents * SUBSCRIPTION_DISCOUNT;

  const addonCents = addons.reduce((sum, addon) => {
    const qty = member.addons?.[addon.dishId] || 0;
    return sum + qty * (Number(addon.price) || 0) * 100;
  }, 0);

  return Math.round(discountedMealCents + addonCents);
}

function formatDollars(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

function Stepper({ label, priceLabel, nutrition, qty, onChange }) {
  return (
    <div className="sf-sub-stepper">
      <div className="sf-sub-stepper__top">
        <div className="sf-sub-stepper__label">
          <span className="sf-sub-stepper__name">{label}</span>
          <span className="sf-sub-stepper__price">{priceLabel}</span>
        </div>
        <div className="sf-sub-stepper__controls">
          <button
            type="button"
            className="sf-sub-stepper__btn"
            aria-label={`Decrease ${label}`}
            onClick={() => onChange(Math.max(0, qty - 1))}
            disabled={qty <= 0}
          >
            −
          </button>
          <span className="sf-sub-stepper__qty">{qty}</span>
          <button
            type="button"
            className="sf-sub-stepper__btn"
            aria-label={`Increase ${label}`}
            onClick={() => onChange(qty + 1)}
          >
            +
          </button>
        </div>
      </div>
      {/* Full-width, below the name/price/stepper row — not squeezed into the
          left column next to the +/- buttons. Calories and the macro
          breakdown are always on separate lines (forced, not left to wrap on
          its own), per request. */}
      {nutrition && (
        <div className="sf-sub-stepper__nutrition">
          <div>{Math.round(nutrition.calories)} kcals</div>
          <div>{Math.round(nutrition.carbsG)}g carbs, {Math.round(nutrition.proteinG)}g protein, {Math.round(nutrition.fatG)}g fat, {Math.round(nutrition.fiberG)}g fiber</div>
        </div>
      )}
    </div>
  );
}

export default function SubscriptionMemberPanel({ subscription, householdMember, addons = [], addonsLoading = false, defaultMealTargets = {}, onChangeMember }) {
  const { shouldShowArrows, getScrollState, scrollLeft, scrollRight, handleScroll } = useScrollArrows();
  const [infoAddonId, setInfoAddonId] = useState(null);
  const infoAddon = addons.find((a) => a.dishId === infoAddonId) || null;

  const updateMeal = (key, qty) => {
    onChangeMember({ ...subscription, meals: { ...subscription.meals, [key]: qty } });
  };

  const updateAddon = (addonId, qty) => {
    const addons = { ...subscription.addons };
    if (qty <= 0) {
      delete addons[addonId];
    } else {
      addons[addonId] = qty;
    }
    onChangeMember({ ...subscription, addons });
  };

  const subtotalCents = memberSubtotalCents(subscription, addons);

  return (
    <div className="sf-sub-panel">
      <section className="sf-sub-section">
        <h3 className="sf-sub-section__title">Weekly meals</h3>
        <p className="sf-sub-section__hint">
          Choose how many of each meal per week. The actual dishes are picked
          from each week&apos;s menu closer to delivery — this just sets quantities.
        </p>
        <div className="sf-sub-stepper-grid">
          {MEAL_TYPES.map(({ key, label, mealType }) => {
            const nutrition = nutritionInfoForMealType(mealType, householdMember, defaultMealTargets);
            return (
              <Stepper
                key={key}
                label={label}
                priceLabel={`${formatDollars(SUBSCRIPTION_BASE_PRICES[key] * 100 * SUBSCRIPTION_DISCOUNT)} ea (10% off)`}
                nutrition={nutrition}
                qty={subscription.meals?.[key] || 0}
                onChange={(qty) => updateMeal(key, qty)}
              />
            );
          })}
        </div>
      </section>

      <section className="sf-sub-section">
        <h3 className="sf-sub-section__title">Add-ons</h3>
        <p className="sf-sub-section__hint">Extra protein or items on top of your meals.</p>
        {addonsLoading && <p className="sf-sub-section__hint">Loading add-ons…</p>}
        {/* Same CompactMealCard + ScrollArrows/useScrollArrows the a-la-carte
            menu uses for each section — one scrollable row with arrows that
            appear once there are more cards than fit, not a wrapping grid. */}
        <div className="sf-menu-scroll-wrap">
          <div
            className="sf-menu-scroll-row"
            data-meal-type={ADDONS_SCROLL_KEY}
            onScroll={(e) => handleScroll(ADDONS_SCROLL_KEY, e)}
          >
            {addons.map((addon) => (
              <CompactMealCard
                key={addon.dishId}
                meal={{
                  ...addon,
                  itemName: addon.name,
                  quantity: subscription.addons?.[addon.dishId] || 0,
                }}
                actualIndex={addon.dishId}
                // AddonInfoModal, not the real MealModal — see its own comment
                // for why (no ingredient substitution applies to a fixed-recipe
                // add-on item).
                onOpenModal={() => setInfoAddonId(addon.dishId)}
                onQuantityChange={(_, qty) => updateAddon(addon.dishId, qty)}
                isLocked={false}
                saving={false}
                addLabel="Add"
                // Details live in AddonInfoModal now (click the card) — the
                // inline ingredient listing was redundant clutter here.
                showIngredients={false}
              />
            ))}
          </div>
          <ScrollArrows
            mealType={ADDONS_SCROLL_KEY}
            shouldShowArrows={shouldShowArrows}
            itemCount={addons.length}
            scrollState={getScrollState(ADDONS_SCROLL_KEY)}
            onScrollLeft={scrollLeft}
            onScrollRight={scrollRight}
          />
        </div>
      </section>

      <div className="sf-sub-panel__subtotal">
        <span>Weekly subtotal for this person</span>
        <strong>{formatDollars(subtotalCents)}</strong>
      </div>

      {infoAddon && (
        <AddonInfoModal
          addon={infoAddon}
          qty={subscription.addons?.[infoAddon.dishId] || 0}
          onChangeQty={(qty) => updateAddon(infoAddon.dishId, qty)}
          onClose={() => setInfoAddonId(null)}
        />
      )}
    </div>
  );
}
